// Public booking page
//

import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import {
  createBooking,
  getBookingAvailability,
  cancelBooking,
} from '../../services/calendarService'

const formatSlot = (slot) => {
  const start = new Date(slot.start)
  const end = new Date(slot.end)
  return `${start.toUTCString()} - ${end.toUTCString()}`
}

const Booking = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [slots, setSlots] = useState([])
  const [booking, setBooking] = useState(null)

  const loadAvailability = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getBookingAvailability(token)
      setSlots(data?.slots || [])
    } catch (err) {
      setError('No fue posible cargar la disponibilidad.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (!token) {
      setError('Token inválido o faltante.')
      setLoading(false)
      return
    }
    loadAvailability()
  }, [loadAvailability, token])

  const handleReserve = async (slot) => {
    setError('')
    try {
      const bookingResponse = await createBooking(token, {
        startAt: slot.start,
        endAt: slot.end,
        title: 'Booking',
      })
      setBooking(bookingResponse)
      await loadAvailability()
    } catch (err) {
      setError('No fue posible crear la reserva.')
    }
  }

  const handleCancel = async () => {
    if (!booking?.id) return
    setError('')
    try {
      await cancelBooking(token, { eventId: booking.id })
      setBooking(null)
      await loadAvailability()
    } catch (err) {
      setError('No fue posible cancelar la reserva.')
    }
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Reserva de cita
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {booking ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Reserva creada. Evento #{booking.id}
            </Alert>
          ) : null}

          {slots.length === 0 ? (
            <Typography variant="body2">No hay slots disponibles.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {slots.map((slot) => (
                <Box
                  key={`${slot.start}-${slot.end}`}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">{formatSlot(slot)}</Typography>
                  <Button
                    variant="contained"
                    onClick={() => handleReserve(slot)}
                  >
                    Reservar
                  </Button>
                </Box>
              ))}
            </Box>
          )}

          {booking ? (
            <Box sx={{ mt: 3 }}>
              <Button color="error" variant="outlined" onClick={handleCancel}>
                Cancelar reserva
              </Button>
            </Box>
          ) : null}
        </Box>
      )}
    </Container>
  )
}

export default Booking
